// File: app/src/components/common/ConfettiCelebration.js
//
// Fires a celebratory confetti burst when mounted.
// Uses canvas-confetti for a lightweight, performant particle effect.
// Re-fires whenever `trigger` changes (truthy).

import { useEffect, useRef } from "react";
import confetti from "canvas-confetti";

const DEFAULTS = {
  spread: 80,
  ticks: 100,
  gravity: 0.9,
  decay: 0.92,
  startVelocity: 35,
  colors: [
    "#a786ff", // purple
    "#fd8bbc", // pink
    "#eca184", // peach
    "#f8deb1", // gold
    "#67e8f9", // cyan
    "#34d399", // emerald
    "#fbbf24", // amber
  ],
};

const fire = (particleRatio, opts) => {
  confetti({
    ...DEFAULTS,
    ...opts,
    particleCount: Math.floor(200 * particleRatio),
  });
};

/**
 * Fires a two-stage confetti burst.
 * Stage 1: Wide low-velocity fan
 * Stage 2: Delayed narrow high-velocity burst
 */
const celebrationBurst = () => {
  // Stage 1 — wide fan from centre
  fire(0.35, { spread: 100, startVelocity: 30, origin: { y: 0.7 } });
  fire(0.25, { spread: 60, startVelocity: 45, origin: { y: 0.7 } });

  // Stage 2 — delayed pop from sides
  setTimeout(() => {
    fire(0.2, { spread: 120, startVelocity: 40, origin: { x: 0.2, y: 0.6 } });
    fire(0.2, { spread: 120, startVelocity: 40, origin: { x: 0.8, y: 0.6 } });
  }, 250);

  // Stage 3 — final shower
  setTimeout(() => {
    fire(0.15, { spread: 160, startVelocity: 25, decay: 0.95, origin: { y: 0.5 } });
  }, 500);
};

const ConfettiCelebration = ({ trigger = true }) => {
  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (trigger && !hasFiredRef.current) {
      hasFiredRef.current = true;
      celebrationBurst();
    }
  }, [trigger]);

  // Reset when trigger changes to falsy (allows re-triggering)
  useEffect(() => {
    if (!trigger) {
      hasFiredRef.current = false;
    }
  }, [trigger]);

  return null; // No DOM elements — canvas-confetti manages its own canvas
};

export default ConfettiCelebration;
